import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { db } from 'config/firebase'
import { useSnackbar } from '@/hooks/common/useSnackbar'
import { deleteAccessCode, deleteAllAccessCodesForPhotoUpload } from '@/core/photoUploader/deletePhotoUpload'
import {
    collection,
    doc,
    DocumentSnapshot,
    getDocs,
    limit,
    orderBy,
    query,
    startAfter,
    writeBatch
} from 'firebase/firestore'
import { useCallback, useState } from 'react'
import { AccessCode } from '@otw/models/photos'

interface UseAccessCodesOptions {
  pageSize?: number
  enablePagination?: boolean
}

export const useAccessCodes = (options: UseAccessCodesOptions = {}) => {
  const { pageSize = 5, enablePagination = true } = options
  const queryClient = useQueryClient()
  const { showSuccess, showError } = useSnackbar()
  
  // Pagination state
  const [lastDoc, setLastDoc] = useState<DocumentSnapshot | null>(null)
  const [hasMore, setHasMore] = useState(true)

  // Fetch access codes with pagination and search
  const {
    data: accessCodes = [],
    isLoading,
    error,
    refetch
  } = useQuery({
    queryKey: ['accessCodes'],
    queryFn: async (): Promise<AccessCode[]> => {
      const accessCodesRef = collection(db, 'accessCodes')
      const q = query(accessCodesRef, orderBy('createdAt', 'desc'))
      const snapshot = await getDocs(q)
      
      return snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      })) as AccessCode[]
    },
    staleTime: 30 * 1000, // 30 seconds
    refetchOnWindowFocus: true,
  })

  // Search access codes by VIN 
  const searchAccessCodes = useCallback(async (searchTerm: string): Promise<AccessCode[]> => {
    if (!searchTerm.trim()) {
      return accessCodes
    }

    const searchLower = searchTerm.toLowerCase()
    
    // Filter through the already loaded access codes
    const filtered = accessCodes.filter(accessCode => {
      // Search by VIN
      const vinMatch = accessCode.vehicles?.some(vehicle =>
        vehicle.vin?.toLowerCase().includes(searchLower)
      )
      
      // Search by access code itself
      const codeMatch = accessCode.code?.toLowerCase().includes(searchLower)
      
      return vinMatch || codeMatch
    })
    
    return filtered
  }, [accessCodes])

  // Paginated fetch for history component
  const fetchAccessCodesPage = useCallback(async (
    isFirstPage = false,
    searchQuery = ''
  ): Promise<{ data: AccessCode[], hasMore: boolean, lastDoc: DocumentSnapshot | null }> => {
    try {
      if (searchQuery) {
        const results = await searchAccessCodes(searchQuery)
        return { data: results, hasMore: false, lastDoc: null }
      }

      const accessCodesRef = collection(db, 'accessCodes')
      let q = query(accessCodesRef, orderBy('createdAt', 'desc'), limit(pageSize))

      if (!isFirstPage && lastDoc) {
        q = query(accessCodesRef, orderBy('createdAt', 'desc'), startAfter(lastDoc), limit(pageSize))
      }

      const snapshot = await getDocs(q)
      const data = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      })) as AccessCode[]

      const newLastDoc = snapshot.docs[snapshot.docs.length - 1] || null
      const newHasMore = snapshot.docs.length === pageSize

      if (isFirstPage) {
        setLastDoc(newLastDoc)
        setHasMore(newHasMore)
      }

      return { data, hasMore: newHasMore, lastDoc: newLastDoc }
    } catch (error) {
      console.error('Error fetching access codes page:', error)
      throw error
    }
  }, [pageSize, lastDoc, searchAccessCodes])

  // Delete single access code mutation
  const deleteAccessCodeMutation = useMutation({
    mutationFn: async (accessCodeId: string) => {
      await deleteAccessCode(accessCodeId)
    },
    onMutate: async (accessCodeId) => {
      await queryClient.cancelQueries({ queryKey: ['accessCodes'] })
      await queryClient.cancelQueries({ queryKey: ['photoUploads-raw'] })
      
      const previousAccessCodes = queryClient.getQueryData<AccessCode[]>(['accessCodes'])
      const previousPhotoUploads = queryClient.getQueryData<any[]>(['photoUploads-raw'])
      
      // Find which access code we're deleting to get the photoUploadId
      const accessCodeToDelete = previousAccessCodes?.find(code => code.id === accessCodeId)
      
      // Optimistically remove the access code from accessCodes
      queryClient.setQueryData<AccessCode[]>(['accessCodes'], (old) => 
        old?.filter(code => code.id !== accessCodeId) || []
      )
      
      // Optimistically remove the access code from photoUploads
      if (accessCodeToDelete) {
        queryClient.setQueryData<any[]>(['photoUploads-raw'], (old) => 
          old?.map(upload => {
            if (upload.id === accessCodeToDelete.photoUploadId) {
              return { ...upload, accessCode: undefined }
            }
            return upload
          }) || []
        )
      }
      
      return { previousAccessCodes, previousPhotoUploads, accessCodeToDelete }
    },
    onError: (error, accessCodeId, context) => {
      if (context?.previousAccessCodes) {
        queryClient.setQueryData(['accessCodes'], context.previousAccessCodes)
      }
      if (context?.previousPhotoUploads) {
        queryClient.setQueryData(['photoUploads-raw'], context.previousPhotoUploads)
      }
      console.error('Error deleting access code:', error)
      showError('Error deleting access code')
    },
    onSuccess: () => {
      showSuccess('Access code deleted successfully')
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ['accessCodes'] })
      queryClient.invalidateQueries({ queryKey: ['photoUploads-raw'] })
    }
  })

  // Delete all access codes for a photo upload
  const deleteAllAccessCodesForPhotoUploadMutation = useMutation({
    mutationFn: async (photoUploadId: string) => {
      await deleteAllAccessCodesForPhotoUpload(photoUploadId)
    },
    onMutate: async (photoUploadId) => {
      await queryClient.cancelQueries({ queryKey: ['accessCodes'] })
      await queryClient.cancelQueries({ queryKey: ['photoUploads-raw'] })
      
      const previousAccessCodes = queryClient.getQueryData<AccessCode[]>(['accessCodes'])
      const previousPhotoUploads = queryClient.getQueryData<any[]>(['photoUploads-raw'])
      
      // Optimistically remove access codes for this photo upload
      queryClient.setQueryData<AccessCode[]>(['accessCodes'], (old) => 
        old?.filter(code => code.photoUploadId !== photoUploadId) || []
      )
      
      // Optimistically remove access code from the photo upload document
      queryClient.setQueryData<any[]>(['photoUploads-raw'], (old) => 
        old?.map(upload => {
          if (upload.id === photoUploadId) {
            return { ...upload, accessCode: undefined }
          }
          return upload
        }) || []
      )
      
      return { previousAccessCodes, previousPhotoUploads }
    },
    onError: (error, photoUploadId, context) => {
      if (context?.previousAccessCodes) {
        queryClient.setQueryData(['accessCodes'], context.previousAccessCodes)
      }
      if (context?.previousPhotoUploads) {
        queryClient.setQueryData(['photoUploads-raw'], context.previousPhotoUploads)
      }
      console.error('Error deleting access codes for photo upload:', error)
      showError('Error deleting access codes')
    },
    onSuccess: () => {
      showSuccess('Access codes deleted successfully')
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ['accessCodes'] })
      queryClient.invalidateQueries({ queryKey: ['photoUploads-raw'] })
    }
  })

  // Delete multiple access codes (for bulk operations)
  const deleteMultipleAccessCodesMutation = useMutation({
    mutationFn: async (accessCodeIds: string[]) => {
      const batch = writeBatch(db)
      
      accessCodeIds.forEach(id => {
        batch.delete(doc(db, 'accessCodes', id))
      })
      
      await batch.commit()
    },
    onMutate: async (accessCodeIds) => {
      await queryClient.cancelQueries({ queryKey: ['accessCodes'] })
      await queryClient.cancelQueries({ queryKey: ['photoUploads-raw'] })
      
      const previousAccessCodes = queryClient.getQueryData<AccessCode[]>(['accessCodes'])
      const previousPhotoUploads = queryClient.getQueryData<any[]>(['photoUploads-raw'])
      
      // Find which photo uploads will be affected
      const accessCodesToDelete = previousAccessCodes?.filter(code => accessCodeIds.includes(code.id)) || []
      const affectedPhotoUploadIds = new Set(accessCodesToDelete.map(code => code.photoUploadId))
      
      // Optimistically remove the access codes
      queryClient.setQueryData<AccessCode[]>(['accessCodes'], (old) => 
        old?.filter(code => !accessCodeIds.includes(code.id)) || []
      )
      
      // Optimistically remove access codes from affected photo upload documents
      queryClient.setQueryData<any[]>(['photoUploads-raw'], (old) => 
        old?.map(upload => {
          if (affectedPhotoUploadIds.has(upload.id)) {
            return { ...upload, accessCode: undefined }
          }
          return upload
        }) || []
      )
      
      return { previousAccessCodes, previousPhotoUploads }
    },
    onError: (error, accessCodeIds, context) => {
      if (context?.previousAccessCodes) {
        queryClient.setQueryData(['accessCodes'], context.previousAccessCodes)
      }
      if (context?.previousPhotoUploads) {
        queryClient.setQueryData(['photoUploads-raw'], context.previousPhotoUploads)
      }
      console.error('Error deleting multiple access codes:', error)
      showError('Error deleting access codes')
    },
    onSuccess: (_, accessCodeIds) => {
      showSuccess(`${accessCodeIds.length} access codes deleted successfully`)
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ['accessCodes'] })
      queryClient.invalidateQueries({ queryKey: ['photoUploads-raw'] })
    }
  })

  // Delete all access codes (for remove all functionality)
  const deleteAllAccessCodesMutation = useMutation({
    mutationFn: async () => {
      const accessCodesRef = collection(db, 'accessCodes')
      const snapshot = await getDocs(query(accessCodesRef, orderBy('createdAt', 'desc')))
      
      if (snapshot.empty) {
        return 0
      }

      // Process in batches to avoid Firestore limits
      const batchSize = 500
      const batches = []
      let currentBatch = writeBatch(db)
      let operationCount = 0

      snapshot.docs.forEach(doc => {
        currentBatch.delete(doc.ref)
        operationCount++

        if (operationCount === batchSize) {
          batches.push(currentBatch)
          currentBatch = writeBatch(db)
          operationCount = 0
        }
      })

      if (operationCount > 0) {
        batches.push(currentBatch)
      }

      await Promise.all(batches.map(batch => batch.commit()))
      return snapshot.size
    },
    onMutate: async () => {
      await queryClient.cancelQueries({ queryKey: ['accessCodes'] })
      await queryClient.cancelQueries({ queryKey: ['photoUploads-raw'] })
      
      const previousAccessCodes = queryClient.getQueryData<AccessCode[]>(['accessCodes'])
      const previousPhotoUploads = queryClient.getQueryData<any[]>(['photoUploads-raw'])
      
      // Optimistically clear all access codes
      queryClient.setQueryData<AccessCode[]>(['accessCodes'], [])
      
      // Optimistically remove access codes from all photo upload documents
      queryClient.setQueryData<any[]>(['photoUploads-raw'], (old) => 
        old?.map(upload => ({ ...upload, accessCode: undefined })) || []
      )
      
      return { previousAccessCodes, previousPhotoUploads }
    },
    onError: (error, _, context) => {
      if (context?.previousAccessCodes) {
        queryClient.setQueryData(['accessCodes'], context.previousAccessCodes)
      }
      if (context?.previousPhotoUploads) {
        queryClient.setQueryData(['photoUploads-raw'], context.previousPhotoUploads)
      }
      console.error('Error deleting all access codes:', error)
      showError('Error deleting all access codes')
    },
    onSuccess: (deletedCount) => {
      showSuccess(`${deletedCount} access codes deleted successfully`)
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ['accessCodes'] })
      queryClient.invalidateQueries({ queryKey: ['photoUploads-raw'] })
    }
  })

  // Get total count of access codes
  const getTotalCount = useCallback(async (): Promise<number> => {
    try {
      const accessCodesRef = collection(db, 'accessCodes')
      const snapshot = await getDocs(query(accessCodesRef))
      return snapshot.size
    } catch (error) {
      console.error('Error getting total count:', error)
      return 0
    }
  }, [])

  // Generate access link for a given UUID
  const getAccessLink = useCallback((uuid: string) => {
    let baseUrl = process.env.VITE_ENV === 'production' 
      ? 'https://uploader.optictruckworks.com/' 
      : 'https://stg.uploader.optictruckworks.com'

    // if (process.env.NODE_ENV === 'development') {
    //   baseUrl = `${process.env.VITE_MOBILE_TESTING_IP || 'http://192.168.1.3'}:3001`
    // }

    return `${baseUrl}/access/${uuid}`
  }, [])

  return {
    // Data
    accessCodes,
    isLoading,
    error,
    hasMore,
    lastDoc,

    // Actions
    searchAccessCodes,
    fetchAccessCodesPage,
    deleteAccessCode: deleteAccessCodeMutation.mutateAsync,
    deleteAllAccessCodesForPhotoUpload: deleteAllAccessCodesForPhotoUploadMutation.mutateAsync,
    deleteMultipleAccessCodes: deleteMultipleAccessCodesMutation.mutateAsync,
    deleteAllAccessCodes: deleteAllAccessCodesMutation.mutateAsync,
    getTotalCount,
    getAccessLink,
    refetch,

    // Loading states
    isDeletingAccessCode: deleteAccessCodeMutation.isPending,
    isDeletingAllForPhotoUpload: deleteAllAccessCodesForPhotoUploadMutation.isPending,
    isDeletingMultiple: deleteMultipleAccessCodesMutation.isPending,
    isDeletingAll: deleteAllAccessCodesMutation.isPending,

    // Pagination helpers
    setLastDoc,
    setHasMore,
  }
} 